import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AnnouncementPopup.css';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const DISMISSED_KEY = 'taillink_dismissed_announcements';

const getDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); }
  catch { return []; }
};
const setDismissed = (id) => {
  const list = getDismissed();
  if (!list.includes(id)) { list.push(id); localStorage.setItem(DISMISSED_KEY, JSON.stringify(list)); }
};

const AnnouncementPopup = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/announcements/active`);
      const all = res.data.announcements || [];
      const dismissed = getDismissed();
      const toShow = all.filter(a => !(a.show_once && dismissed.includes(a.id)));
      if (toShow.length > 0) {
        setAnnouncements(toShow);
        setCurrentIndex(0);
        // Small delay before showing
        setTimeout(() => setVisible(true), 800);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDismiss = () => {
    const current = announcements[currentIndex];
    if (current) setDismissed(current.id);

    setAnimating(true);
    setTimeout(() => {
      if (currentIndex < announcements.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setAnimating(false);
      } else {
        setVisible(false);
        setAnimating(false);
      }
    }, 250);
  };

  if (!visible || announcements.length === 0) return null;

  const ann = announcements[currentIndex];
  if (!ann) return null;

  const hasImage = ann.image_url || ann.image_base64;
  const imgSrc = ann.image_base64 || ann.image_url;

  return (
    <div className="ann-overlay" onClick={handleDismiss} data-testid="announcement-overlay">
      <div
        className={`ann-popup ${animating ? 'ann-popup-exit' : 'ann-popup-enter'}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="announcement-popup"
      >
        {/* Close Button */}
        <button className="ann-close" onClick={handleDismiss} data-testid="announcement-close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Image */}
        {hasImage && (
          <div className="ann-image-wrap">
            <img src={imgSrc} alt="" className="ann-image" data-testid="announcement-image" />
          </div>
        )}

        {/* Content */}
        <div className="ann-content">
          <h2 className="ann-title" data-testid="announcement-title">{ann.title}</h2>
          <div className="ann-body" data-testid="announcement-content" dangerouslySetInnerHTML={{ __html: ann.content.replace(/\n/g, '<br/>') }} />

          {ann.button_text && ann.button_url && (
            <a
              href={ann.button_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ann-cta-btn"
              data-testid="announcement-cta"
            >
              {ann.button_text}
            </a>
          )}

          <button className="ann-dismiss-btn" onClick={handleDismiss} data-testid="announcement-dismiss">
            {announcements.length > 1 && currentIndex < announcements.length - 1
              ? `ถัดไป (${currentIndex + 1}/${announcements.length})`
              : 'ปิด'}
          </button>
        </div>

        {/* Pagination Dots */}
        {announcements.length > 1 && (
          <div className="ann-dots">
            {announcements.map((_, i) => (
              <div key={i} className={`ann-dot ${i === currentIndex ? 'active' : ''}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementPopup;
